/**
 * ImportCsvDialog — reusable CSV batch-import dialog for Expenses and Invoices.
 *
 * Supports two modes:
 *   - "expense"  → vendor, date, amount, currency, category, notes
 *   - "invoice"  → counterparty, direction, amount, currency, issued_on, due_on, status, notes
 *
 * Usage:
 *   <ImportCsvDialog
 *     mode="expense"
 *     open={open}
 *     onOpenChange={setOpen}
 *     onSuccess={(count) => showBanner(count)}
 *   />
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Download, X, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useCategories } from "../../lib/categories/useCategories";
import type { NewExpenseInput } from "../../lib/expenses/types";
import type { NewInvoiceInput } from "../../lib/invoices/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ImportMode = "expense" | "invoice";

interface ImportCsvDialogProps {
  mode: ImportMode;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Called after addMany succeeds with the number of imported rows. */
  onSuccess: (count: number) => void;
}

interface ParsedRow {
  /** raw column values keyed by header (lower-cased, trimmed) */
  cells: Record<string, string>;
  /** 1-based row number for error messages (includes header row) */
  lineNumber: number;
}

interface ValidationError {
  lineNumber: number;
  field: string;
  message: string;
}

// A validated, ready-to-import expense row.
interface ValidExpenseRow {
  vendor: string;
  occurredOn: string;
  amountCents: number;
  currency: string;
  categoryId: string | null;
  categoryLabel: string | null;
  notes: string | undefined;
}

// A validated, ready-to-import invoice row.
interface ValidInvoiceRow {
  counterparty: string;
  direction: "incoming" | "outgoing";
  amountCents: number;
  currency: string;
  issuedOn: string;
  dueOn: string | null;
  status: NewInvoiceInput["status"];
  notes: string | undefined;
}

type ValidRow = ValidExpenseRow | ValidInvoiceRow;

// ---------------------------------------------------------------------------
// Simple CSV parser — handles quoted fields, newlines inside quotes, CRLF
// ---------------------------------------------------------------------------

function parseCsvText(text: string): { headers: string[]; rows: ParsedRow[] } {
  const normalised = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Tokenise character by character so we can respect quoted fields.
  const records: string[][] = [];
  let current: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  while (i < normalised.length) {
    const ch = normalised[i]!;

    if (inQuotes) {
      if (ch === '"') {
        // Escaped quote?
        if (normalised[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        current.push(field.trim());
        field = "";
      } else if (ch === "\n") {
        current.push(field.trim());
        field = "";
        if (current.some((c) => c !== "")) {
          records.push(current);
        }
        current = [];
        i++;
        continue;
      } else {
        field += ch;
      }
    }
    i++;
  }
  // Last field / record
  current.push(field.trim());
  if (current.some((c) => c !== "")) {
    records.push(current);
  }

  if (records.length === 0) return { headers: [], rows: [] };

  const rawHeaders = records[0]!;
  const headers = rawHeaders.map((h) => h.toLowerCase().trim());

  const rows: ParsedRow[] = records.slice(1).map((cols, idx) => {
    const cells: Record<string, string> = {};
    headers.forEach((h, ci) => {
      cells[h] = cols[ci]?.trim() ?? "";
    });
    return { cells, lineNumber: idx + 2 }; // +2: 1-based, skip header
  });

  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Amount parsing — dollars or cents heuristic
// ---------------------------------------------------------------------------

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const num = parseFloat(cleaned);
  if (!isFinite(num) || num < 0) return null;
  // If the value is > 10000, treat as already in cents, otherwise * 100.
  if (num > 10000) return Math.round(num);
  return Math.round(num * 100);
}

// ---------------------------------------------------------------------------
// Date validation
// ---------------------------------------------------------------------------

function isValidDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s));
}

// ---------------------------------------------------------------------------
// Template generators
// ---------------------------------------------------------------------------

function buildExpenseTemplate(): string {
  return [
    "vendor,date,amount,currency,category,notes",
    "Stripe,2026-03-15,129.00,USD,Software,Monthly billing",
    "AWS,2026-03-20,340.50,USD,Infrastructure,Q1 usage",
  ].join("\n");
}

function buildInvoiceTemplate(): string {
  return [
    "counterparty,direction,amount,currency,issued_on,due_on,status,notes",
    "Acme Corp,outgoing,5000.00,USD,2026-03-01,2026-03-31,pending,Q1 services",
    "GlobalTech,incoming,2500.00,USD,2026-03-10,2026-04-10,pending,Consulting",
  ].join("\n");
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

function requiredField(
  cells: Record<string, string>,
  key: string,
  label: string,
  lineNumber: number,
  errors: ValidationError[],
): string | null {
  const val = cells[key];
  if (!val || val.trim() === "") {
    errors.push({ lineNumber, field: key, message: `${label} is required` });
    return null;
  }
  return val.trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ImportCsvDialog({
  mode,
  open,
  onOpenChange,
  onSuccess,
}: ImportCsvDialogProps) {
  const { addMany: addManyExpenses } = useExpenses();
  const { addMany: addManyInvoices } = useInvoices();
  const { categories } = useCategories();

  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [validRows, setValidRows] = useState<ValidRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setDragActive(false);
      setFileName(null);
      setParseError(null);
      setValidationErrors([]);
      setValidRows([]);
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  // ---------------------------------------------------------------------------
  // File processing
  // ---------------------------------------------------------------------------

  function processFile(file: File): void {
    setParseError(null);
    setValidationErrors([]);
    setValidRows([]);
    setFileName(file.name);

    if (!file.name.match(/\.(csv|tsv|txt)$/i)) {
      setParseError("Please upload a .csv, .tsv, or .txt file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text !== "string") {
        setParseError("Failed to read file.");
        return;
      }

      let parsed: ReturnType<typeof parseCsvText>;
      try {
        parsed = parseCsvText(text);
      } catch {
        setParseError("Could not parse the CSV file.");
        return;
      }

      if (parsed.rows.length === 0) {
        setParseError("The file has no data rows.");
        return;
      }

      if (mode === "expense") {
        validateExpenseRows(parsed.rows);
      } else {
        validateInvoiceRows(parsed.rows);
      }
    };
    reader.onerror = () => setParseError("Failed to read file.");
    reader.readAsText(file);
  }

  function validateExpenseRows(rows: ParsedRow[]): void {
    const errors: ValidationError[] = [];
    const valid: ValidExpenseRow[] = [];

    for (const row of rows) {
      const { cells, lineNumber } = row;
      const rowErrors: ValidationError[] = [];

      const vendor = requiredField(cells, "vendor", "Vendor", lineNumber, rowErrors);
      const dateRaw = requiredField(cells, "date", "Date", lineNumber, rowErrors);
      const amountRaw = requiredField(cells, "amount", "Amount", lineNumber, rowErrors);

      if (dateRaw && !isValidDate(dateRaw)) {
        rowErrors.push({
          lineNumber,
          field: "date",
          message: "Date must be YYYY-MM-DD format",
        });
      }

      let amountCents: number | null = null;
      if (amountRaw) {
        amountCents = parseAmount(amountRaw);
        if (amountCents === null || amountCents <= 0) {
          rowErrors.push({
            lineNumber,
            field: "amount",
            message: "Amount must be a positive number",
          });
        }
      }

      errors.push(...rowErrors);

      if (
        rowErrors.length === 0 &&
        vendor &&
        dateRaw &&
        amountCents !== null
      ) {
        // Resolve category
        const catLabel = cells["category"]?.trim() ?? "";
        const matchedCat =
          categories.find(
            (c) => c.label.toLowerCase() === catLabel.toLowerCase(),
          ) ?? categories[0] ?? null;

        const currency = cells["currency"]?.trim() || "USD";
        const notes = cells["notes"]?.trim() || undefined;

        valid.push({
          vendor,
          occurredOn: dateRaw,
          amountCents,
          currency,
          categoryId: matchedCat?.id ?? null,
          categoryLabel: matchedCat?.label ?? null,
          notes,
        });
      }
    }

    setValidationErrors(errors);
    setValidRows(valid);
  }

  function validateInvoiceRows(rows: ParsedRow[]): void {
    const errors: ValidationError[] = [];
    const valid: ValidInvoiceRow[] = [];

    const validDirections = new Set(["incoming", "outgoing"]);
    const validStatuses = new Set([
      "draft",
      "pending",
      "approved",
      "rejected",
      "paid",
      "overdue",
      "cancelled",
    ]);

    for (const row of rows) {
      const { cells, lineNumber } = row;
      const rowErrors: ValidationError[] = [];

      const counterparty = requiredField(
        cells,
        "counterparty",
        "Counterparty",
        lineNumber,
        rowErrors,
      );
      const issuedOnRaw = requiredField(
        cells,
        "issued_on",
        "Issued on",
        lineNumber,
        rowErrors,
      );
      const amountRaw = requiredField(
        cells,
        "amount",
        "Amount",
        lineNumber,
        rowErrors,
      );

      if (issuedOnRaw && !isValidDate(issuedOnRaw)) {
        rowErrors.push({
          lineNumber,
          field: "issued_on",
          message: "Issued on must be YYYY-MM-DD format",
        });
      }

      const dueOnRaw = cells["due_on"]?.trim() || null;
      if (dueOnRaw && !isValidDate(dueOnRaw)) {
        rowErrors.push({
          lineNumber,
          field: "due_on",
          message: "Due on must be YYYY-MM-DD format",
        });
      }

      let amountCents: number | null = null;
      if (amountRaw) {
        amountCents = parseAmount(amountRaw);
        if (amountCents === null || amountCents <= 0) {
          rowErrors.push({
            lineNumber,
            field: "amount",
            message: "Amount must be a positive number",
          });
        }
      }

      const directionRaw = (cells["direction"]?.trim() || "incoming").toLowerCase();
      if (!validDirections.has(directionRaw)) {
        rowErrors.push({
          lineNumber,
          field: "direction",
          message: 'Direction must be "incoming" or "outgoing"',
        });
      }

      const statusRaw = (cells["status"]?.trim() || "pending").toLowerCase();
      if (!validStatuses.has(statusRaw)) {
        rowErrors.push({
          lineNumber,
          field: "status",
          message: "Invalid status value",
        });
      }

      errors.push(...rowErrors);

      if (
        rowErrors.length === 0 &&
        counterparty &&
        issuedOnRaw &&
        amountCents !== null
      ) {
        const currency = cells["currency"]?.trim() || "USD";
        const notes = cells["notes"]?.trim() || undefined;

        valid.push({
          counterparty,
          direction: directionRaw as "incoming" | "outgoing",
          amountCents,
          currency,
          issuedOn: issuedOnRaw,
          dueOn: dueOnRaw,
          status: statusRaw as NewInvoiceInput["status"],
          notes,
        });
      }
    }

    setValidationErrors(errors);
    setValidRows(valid);
  }

  // ---------------------------------------------------------------------------
  // Drag-and-drop + file input handlers
  // ---------------------------------------------------------------------------

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories],
  );

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    // Reset so same file can be re-uploaded
    e.target.value = "";
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async function handleSubmit(): Promise<void> {
    if (validRows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (mode === "expense") {
        const inputs: NewExpenseInput[] = (validRows as ValidExpenseRow[]).map(
          (r) => ({
            vendor: r.vendor,
            occurredOn: r.occurredOn,
            amountCents: r.amountCents,
            currency: r.currency,
            categoryId: r.categoryId,
            categoryLabel: r.categoryLabel,
            businessPurpose: r.notes,
          }),
        );
        await addManyExpenses(inputs);
      } else {
        const inputs: NewInvoiceInput[] = (validRows as ValidInvoiceRow[]).map(
          (r) => ({
            counterparty: r.counterparty,
            direction: r.direction,
            amountCents: r.amountCents,
            currency: r.currency,
            issuedOn: r.issuedOn,
            dueOn: r.dueOn,
            status: r.status,
            notes: r.notes,
          }),
        );
        await addManyInvoices(inputs);
      }
      onSuccess(validRows.length);
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const previewRows = validRows.slice(0, 5);
  const extraCount = validRows.length - previewRows.length;
  const hasErrors = validationErrors.length > 0;
  const canImport = validRows.length > 0 && !submitting;

  const entityLabel = mode === "expense" ? "Expense" : "Invoice";
  const templateFilename =
    mode === "expense" ? "expense-import-template.csv" : "invoice-import-template.csv";

  function downloadTemplate(): void {
    const content =
      mode === "expense" ? buildExpenseTemplate() : buildInvoiceTemplate();
    downloadCsv(templateFilename, content);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="import-csv-dialog"
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Import {entityLabel}s from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk-import {entityLabel.toLowerCase()}s. Download
            the template to see the expected format.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          {/* Template download */}
          <button
            type="button"
            data-testid="import-csv-download-template"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 self-start text-sm font-medium text-[#0070F2] hover:text-[#005BCA] hover:underline"
          >
            <Download className="w-4 h-4" />
            Download template CSV
          </button>

          {/* Drop zone / file picker */}
          <label
            data-testid="import-csv-dropzone"
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-slate-200 bg-slate-50 hover:border-slate-300"
            }`}
          >
            <Upload className="w-6 h-6 text-slate-400" />
            <div className="text-sm text-slate-700 font-medium">
              {fileName ? (
                <span className="text-slate-900">{fileName}</span>
              ) : (
                "Drag & drop a CSV here, or click to browse"
              )}
            </div>
            <div className="text-xs text-slate-500">.csv, .tsv, .txt</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              className="sr-only"
              data-testid="import-csv-file-input"
              onChange={handleFileChange}
            />
          </label>

          {/* Parse error */}
          {parseError && (
            <div
              data-testid="import-csv-parse-error"
              className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {parseError}
            </div>
          )}

          {/* Validation errors */}
          {hasErrors && (
            <div
              data-testid="import-csv-validation-errors"
              className="flex flex-col gap-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Validation errors ({validationErrors.length} row
                {validationErrors.length !== 1 ? "s" : ""} skipped)
              </div>
              <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto list-disc list-inside">
                {validationErrors.map((err, i) => (
                  <li key={i}>
                    Row {err.lineNumber}: [{err.field}] {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview table */}
          {validRows.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-green-800">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                {validRows.length} valid row{validRows.length !== 1 ? "s" : ""}{" "}
                ready to import
              </div>

              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                    <tr>
                      {mode === "expense" ? (
                        <>
                          <th className="px-3 py-2 font-semibold">Vendor</th>
                          <th className="px-3 py-2 font-semibold">Date</th>
                          <th className="px-3 py-2 font-semibold">Amount</th>
                          <th className="px-3 py-2 font-semibold">Currency</th>
                          <th className="px-3 py-2 font-semibold">Category</th>
                        </>
                      ) : (
                        <>
                          <th className="px-3 py-2 font-semibold">Counterparty</th>
                          <th className="px-3 py-2 font-semibold">Direction</th>
                          <th className="px-3 py-2 font-semibold">Amount</th>
                          <th className="px-3 py-2 font-semibold">Issued on</th>
                          <th className="px-3 py-2 font-semibold">Status</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {mode === "expense"
                      ? (previewRows as ValidExpenseRow[]).map((row, i) => (
                          <tr key={i} data-testid={`import-csv-preview-row-${i}`}>
                            <td className="px-3 py-2 text-slate-700">{row.vendor}</td>
                            <td className="px-3 py-2 text-slate-700">{row.occurredOn}</td>
                            <td className="px-3 py-2 text-slate-700">
                              {formatCents(row.amountCents, row.currency)}
                            </td>
                            <td className="px-3 py-2 text-slate-700">{row.currency}</td>
                            <td className="px-3 py-2 text-slate-700">
                              {row.categoryLabel ?? "—"}
                            </td>
                          </tr>
                        ))
                      : (previewRows as ValidInvoiceRow[]).map((row, i) => (
                          <tr key={i} data-testid={`import-csv-preview-row-${i}`}>
                            <td className="px-3 py-2 text-slate-700">{row.counterparty}</td>
                            <td className="px-3 py-2 text-slate-700">{row.direction}</td>
                            <td className="px-3 py-2 text-slate-700">
                              {formatCents(row.amountCents, row.currency)}
                            </td>
                            <td className="px-3 py-2 text-slate-700">{row.issuedOn}</td>
                            <td className="px-3 py-2 text-slate-700">{row.status ?? "pending"}</td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>

              {extraCount > 0 && (
                <p className="text-xs text-slate-500 text-right">
                  …and {extraCount} more row{extraCount !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          )}

          {/* Submit error */}
          {submitError && (
            <div
              data-testid="import-csv-submit-error"
              className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="import-csv-submit"
            onClick={() => void handleSubmit()}
            disabled={!canImport}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {submitting
              ? "Importing…"
              : `Import ${validRows.length} row${validRows.length !== 1 ? "s" : ""}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
